/**
 * NfseModule — visão pós-onboarding: lista de NFS-e emitidas + nova emissão.
 * Renderizado quando o perfil fiscal está concluído (onboarding_status = 200).
 *
 * ─── O DESENHO OFICIAL NUMA LISTA FISCAL ────────────────────────────────────
 *
 * Ele renderiza o `NfseHero` (o mesmo componente dos portões da `NfsePage`, ver
 * o cabeçalho de lá) porque é AQUI que a lista existe — e o valor ao vivo do
 * hero é a soma do recorte visível. Os controles são os de `/quotes`,
 * `/recibos` e `/ordens-servico`: busca, ordenação, modal de período/valor e uma
 * faixa de botões de recorte. O que esta tela acrescenta ao molde:
 *
 * · **DOZE status viram CINCO botões.** A máquina de estados da NFS-e tem 12
 *   posições; uma faixa com 12 pílulas rolaria sem fim e quase todas trariam
 *   zero. Os botões agrupam por FAMÍLIA (`nfseListFilters`) e o selo do card
 *   continua dizendo o status exato, que é onde a precisão importa.
 * · **NÃO ganhou modo de seleção nem exportação.** As duas existem em `/quotes`
 *   e `/recibos` porque lá há ações em massa e um arquivo para exportar; aqui
 *   cada nota é um documento do fisco com ações próprias (DANFSE, cancelamento,
 *   reconciliação), todas dentro do diálogo de detalhe. O desenho transfere a
 *   ESTRUTURA, não features que a tela não possui (#198).
 * · **O AMBIENTE fica visível SEMPRE**, inclusive com a lista vazia: emitir em
 *   homologação produz nota que não vale nada, e é a informação que muda o
 *   significado de tudo o que a tela mostra.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowUpDown,
  Check,
  FileText,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useNfse } from '@/hooks/useNfse';
import { NFSE_STATUS_META, type FiscalProfile, type NfseDocument } from '@/types/fiscal';
import { NfseDetailDialog } from './NfseDetailDialog';
import { CertExpiryBanner } from './CertExpiryBanner';
import { NfseHero } from './NfseHero';
import {
  DEFAULT_NFSE_SORT,
  NFSE_PERIOD_PRESETS,
  NFSE_SORT_LABELS,
  NFSE_SORT_ORDER,
  NFSE_STATUS_FILTER_OPTIONS,
  countActiveNfseFilters,
  countNfseByStatus,
  isDeadNfseStatusFilter,
  matchesNfsePeriod,
  matchesNfseStatus,
  matchesNfseValue,
  nfseListTotalLabel,
  nfseMatchesSearch,
  nfseTomadorNome,
  sortNfseBy,
  summarizeNfseList,
  type NfseSortOption,
  type NfseStatusFilter,
} from '@/lib/nfseListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { PERIOD_PRESET_LABELS, resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { CONTENT_CARD } from '@/components/ui/field';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

const brl = (n: number | null | undefined) =>
  Number(n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const toneClass: Record<string, string> = {
  neutral: 'bg-muted text-muted-foreground',
  progress: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

const PERIOD_GROUP_KEY = 'period';

export function NfseModule({
  profile,
  onBack,
}: {
  profile: FiscalProfile;
  onBack: () => void;
}) {
  const { list } = useNfse();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<NfseDocument | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<NfseStatusFilter>('all');
  const [sortOption, setSortOption] = useState<NfseSortOption>(DEFAULT_NFSE_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const notes = useMemo(() => list.data ?? [], [list.data]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e chamá-la
   * por nota compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM o status — é ele que alimenta as contagens dos botões. */
  const notesBeforeStatus = useMemo(
    () =>
      notes.filter(
        (nfse) =>
          nfseMatchesSearch(nfse, search) &&
          matchesNfsePeriod(nfse, periodRange) &&
          matchesNfseValue(nfse, valueRange),
      ),
    [notes, search, periodRange, valueRange],
  );

  const statusCounts = useMemo(() => countNfseByStatus(notesBeforeStatus), [notesBeforeStatus]);

  const visible = useMemo(
    () =>
      sortNfseBy(
        notesBeforeStatus.filter((nfse) => matchesNfseStatus(nfse, statusFilter)),
        sortOption,
      ),
    [notesBeforeStatus, statusFilter, sortOption],
  );

  /**
   * `includeDead` liga quando o recorte É o de dinheiro morto: com o filtro em
   * "Canceladas", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeNfseList(visible, {
    includeDead: isDeadNfseStatusFilter(statusFilter),
  });
  const activeModalFilters = countActiveNfseFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || statusFilter !== 'all' || activeModalFilters > 0;

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Criada em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...NFSE_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  const homologacao = profile.ambiente !== 'prod';

  return (
    <div className="pb-4">
      <NfseHero
        onBack={onBack}
        action={
          /* A ação PRIMÁRIA fica com o rótulo CURTO: "Nova NFS-e" na linha do
             título disputaria espaço com o h1, e sem `flex-wrap` o excedente não
             quebra — ele sai da tela (#186). O nome inteiro vai no
             `aria-label`/`title`, onde não custa pixel nenhum (#142). */
          <Button3D
            size="sm"
            color="light"
            className="shrink-0"
            onClick={() => navigate('/nfse/nova')}
            aria-label="Nova NFS-e"
            title="Nova NFS-e"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nova
          </Button3D>
        }
        summary={{
          label: nfseListTotalLabel(statusFilter),
          total: summary.total,
          subline:
            // Enquanto a lista carrega, o hero NÃO afirma que está vazia: um
            // "Nenhuma nota" antes da resposta é afirmação falsa em cima de um
            // R$ 0,00 igualmente falso.
            (summary.count === 0
              ? list.isLoading
                ? 'Carregando…'
                : 'Nenhuma nota neste filtro'
              : plural(summary.count, 'nota fiscal', 'notas fiscais')) +
            // "no silent caps": o que ficou fora da soma é DITO. Sem isto o
            // total apareceria menor do que a lista sugere, sem explicação.
            (summary.excludedCount > 0
              ? ` · ${plural(
                  summary.excludedCount,
                  'cancelada ou rejeitada fora da conta',
                  'canceladas ou rejeitadas fora da conta',
                )}`
              : ''),
        }}
      />

      {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
      <div className="relative z-10 -mt-9 space-y-5">
        {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e contexto. Eles são um
            grupo só (todos respondem "o que eu estou vendo"), então ficam juntos
            com `space-y-2` em vez do `space-y-5` que separa as SEÇÕES (#191/#194). */}
        <div className="space-y-2">
          {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o campo
              é `min-w-0 flex-1`: sem isso o input empurraria os botões para fora
              da tela em vez de encolher (#186). */}
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Tomador, serviço ou nº da nota"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  aria-label={`Ordenar (${NFSE_SORT_LABELS[sortOption]})`}
                  title={`Ordenar: ${NFSE_SORT_LABELS[sortOption]}`}
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {NFSE_SORT_ORDER.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    onSelect={() => setSortOption(option)}
                    className="gap-2"
                  >
                    {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                        placeholder, o rótulo escolhido fica deslocado. */}
                    <span className="flex h-4 w-4 items-center justify-center">
                      {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                    </span>
                    {NFSE_SORT_LABELS[option]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="icon"
              className="relative h-10 w-10 shrink-0 rounded-xl"
              onClick={() => setFiltersOpen(true)}
              aria-label={
                activeModalFilters > 0
                  ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                  : 'Filtros'
              }
              title="Filtros"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeModalFilters > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                  {activeModalFilters}
                </span>
              )}
            </Button>
          </div>

          {/* STATUS — botões planos, não alternador (#25/#188). O nome COMPLETO
              do recorte e a contagem vão no `title`/`aria-label`, onde não custam
              pixel (#191): "Autoriz." é a abreviação que cabe, não o nome do
              estado — o selo do card, uma linha abaixo, diz "Autorizada". */}
          <div className={FILTER_CHIP_ROW}>
            {NFSE_STATUS_FILTER_OPTIONS.map((option) => {
              const active = statusFilter === option.value;
              // A contagem responde "quantas eu veria se clicasse aqui" — por
              // isso sai do recorte SEM o status.
              const count = statusCounts[option.value] ?? 0;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value)}
                  aria-pressed={active}
                  aria-label={`${option.srLabel} (${count})`}
                  title={`${option.srLabel}: ${plural(count, 'nota', 'notas')}`}
                  className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {/* EMISSOR E AMBIENTE — visível sempre, inclusive com a lista vazia.
              Nota emitida em homologação NÃO vale nada, e essa é a informação
              que muda o significado de tudo o que a tela mostra; por isso ela é
              um selo âmbar, e não mais um pedaço de texto cinza. */}
          <div className="flex flex-wrap items-center gap-2 px-1 text-xs text-muted-foreground">
            <span className="min-w-0 truncate font-medium text-foreground">
              {profile.razao_social ?? 'Empresa'}
            </span>
            <Badge
              variant="outline"
              className={cn(
                'h-5 shrink-0 px-1.5 py-0 text-[10px] font-medium',
                homologacao && 'border-amber-500/40 bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
              )}
            >
              {homologacao ? 'Homologação' : 'Produção'}
            </Badge>
          </div>

          <CertExpiryBanner />

          {/* A ordenação ativa é a única decisão da tela que não se anuncia
              sozinha: status tem botões e período/valor têm o selo do ícone, mas
              "por que esta nota está no topo?" só se responde abrindo o menu. É
              rótulo, não botão — o controle é o ícone logo acima, e um segundo
              gatilho dividiria o alvo do toque. */}
          {!list.isLoading && visible.length > 0 && (
            <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
              <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{NFSE_SORT_LABELS[sortOption]}</span>
            </div>
          )}

          {/* Lista — DENTRO do mesmo grupo `space-y-2` dos controles, e não um
              `-mt-N` competindo com o `space-y-5` de fora (#194). */}
          {list.isLoading && visible.length === 0 ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className={cn(CONTENT_CARD, 'h-24 animate-pulse')} />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div
              className={cn(
                CONTENT_CARD,
                'flex flex-col items-center justify-center border-dashed py-8 text-center',
              )}
            >
              <FileText className="mb-3 h-12 w-12 text-muted-foreground/50" />
              <p className="font-medium">
                {recorteAtivo ? 'Nenhuma nota encontrada' : 'Nenhuma NFS-e emitida ainda'}
              </p>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                {recorteAtivo
                  ? 'Nenhuma nota corresponde à busca e aos filtros escolhidos.'
                  : 'Emita a nota fiscal de serviço direto daqui — ou a partir de um orçamento aprovado.'}
              </p>
              {!recorteAtivo && (
                <Button3D className="mt-5" onClick={() => navigate('/nfse/nova')}>
                  <Plus className="mr-1.5 h-4 w-4" /> Emitir a primeira
                </Button3D>
              )}
            </div>
          ) : (
            /* Duas colunas no desktop (padrão de `/quotes`), com `grid-cols-1`
               explícito no celular (#185). */
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
              {visible.map((n) => {
                // Status fora do vocabulário (linha de cache antiga, schema à
                // frente do bundle) não pode derrubar a lista: sem o fallback,
                // `meta.tone` estoura e a tela inteira some.
                const meta = NFSE_STATUS_META[n.status] ?? {
                  label: String(n.status ?? '—'),
                  tone: 'neutral' as const,
                };
                const tomador = nfseTomadorNome(n);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setSelected(n)}
                    className={cn(
                      CONTENT_CARD,
                      'flex w-full items-center gap-3 p-4 text-left transition-shadow hover:shadow-md',
                    )}
                  >
                    {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é quem
                        dita a altura do card — o `items-center` do card faz o
                        bloco valor+status centrar contra ELA, e não contra a
                        linha do tomador (#189). */}
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {n.numero && (
                          <Badge
                            variant="outline"
                            className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                          >
                            Nº {n.numero}
                          </Badge>
                        )}
                        {n.created_at && (
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                          >
                            {/* `created_at` é TIMESTAMP: o calendário LOCAL é o
                                certo — cortar a string no 'T' daria a data em
                                UTC e imprimiria o DIA SEGUINTE para tudo criado
                                depois das 21h (#114). */}
                            {new Date(n.created_at).toLocaleDateString('pt-BR')}
                          </Badge>
                        )}
                      </div>

                      {/* `min-w-0` + `break-words`, sem `truncate`: é o tomador
                          que distingue duas linhas da mesma lista, e cortá-lo
                          come justamente o que informa (#175). */}
                      <div className="min-w-0 space-y-0.5">
                        <p className="min-w-0 break-words font-bold">
                          {tomador || 'Sem tomador'}
                        </p>
                      </div>
                    </div>

                    {/* Valor + status são UM CONJUNTO, e por isso um bloco só
                        (#189). */}
                    <div className="flex shrink-0 flex-col items-center gap-1">
                      <p className="font-semibold">{brl(n.valor_servico)}</p>
                      <Badge className={cn('border-0', toneClass[meta.tone])}>{meta.label}</Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filtros: quando a nota foi criada e a faixa de valor.
          O STATUS aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte as notas por quando foram criadas e por valor do serviço."
        groups={filterGroups}
        rangeLabel="Valor do serviço"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && NFSE_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
      {selected && (
        <NfseDetailDialog
          nfse={notes.find((n) => n.id === selected.id) ?? selected}
          open={Boolean(selected)}
          onOpenChange={(v) => !v && setSelected(null)}
        />
      )}
    </div>
  );
}
